"use client";

import * as React from "react";
import { ReactRenderer } from "@tiptap/react";
import type { Editor } from "@tiptap/react";

import { cn } from "../../lib/utils";

export type MentionItem = { id: string; label: string };

/** What the host provides: search people for the `@` query (current workspace/branch users). */
export type MentionQuery = (text: string) => Promise<MentionItem[]>;

type MentionListProps = {
  items: MentionItem[];
  command: (item: MentionItem) => void;
};

type MentionListHandle = { onKeyDown: (event: KeyboardEvent) => boolean };

/** The `@`-suggestion dropdown. Keyboard-navigable (↑/↓/Enter/Escape) via an imperative handle the editor calls. */
const MentionList = React.forwardRef<MentionListHandle, MentionListProps>(function MentionList({ items, command }, ref) {
  const [selected, setSelected] = React.useState(0);

  React.useEffect(() => setSelected(0), [items]);

  const pick = (index: number) => {
    const item = items[index];
    if (item) command(item);
  };

  React.useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === "ArrowUp") {
        setSelected((s) => (s + items.length - 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelected((s) => (s + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "Enter") {
        pick(selected);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) return null;

  return (
    <div className="bg-popover text-popover-foreground z-50 max-h-64 w-64 overflow-y-auto rounded-lg border p-1 shadow-[var(--shadow-popover)]">
      {items.map((item, index) => (
        <button
          key={item.id}
          type="button"
          // Use mousedown so the click registers before the editor loses focus / the popup closes.
          onMouseDown={(event) => {
            event.preventDefault();
            command(item);
          }}
          onMouseEnter={() => setSelected(index)}
          className={cn(
            "flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm",
            index === selected ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
          )}
        >
          <span className="truncate">{item.label}</span>
        </button>
      ))}
    </div>
  );
});

/**
 * Build the tiptap `suggestion` config (items + render) for the Mention extension. The list is mounted via
 * ReactRenderer into a fixed-position container anchored at the caret — no extra positioning dependency. The
 * insertion `command` comes from the Mention extension's default suggestion.
 */
export function createMentionSuggestion(query: MentionQuery) {
  return {
    items: async ({ query: text }: { query: string }) => {
      try {
        return await query(text);
      } catch {
        return [];
      }
    },
    render: () => {
      let renderer: ReactRenderer<MentionListHandle, MentionListProps> | null = null;
      let container: HTMLDivElement | null = null;

      const position = (clientRect: (() => DOMRect | null) | null | undefined) => {
        if (!container || !clientRect) return;
        const rect = clientRect();
        if (!rect) return;
        container.style.position = "fixed";
        container.style.left = `${rect.left}px`;
        container.style.top = `${rect.bottom + 4}px`;
        container.style.zIndex = "9999";
      };

      return {
        onStart: (props: { editor: Editor; clientRect?: (() => DOMRect | null) | null; items: MentionItem[]; command: (item: MentionItem) => void }) => {
          renderer = new ReactRenderer(MentionList, { props, editor: props.editor });
          container = document.createElement("div");
          container.appendChild(renderer.element);
          document.body.appendChild(container);
          position(props.clientRect);
        },
        onUpdate: (props: { clientRect?: (() => DOMRect | null) | null; items: MentionItem[]; command: (item: MentionItem) => void }) => {
          renderer?.updateProps(props);
          position(props.clientRect);
        },
        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (props.event.key === "Escape") {
            container?.remove();
            return true;
          }
          return renderer?.ref?.onKeyDown(props.event) ?? false;
        },
        onExit: () => {
          container?.remove();
          container = null;
          renderer?.destroy();
          renderer = null;
        },
      };
    },
  };
}
